/**
 * LatestFromJournal — Home page strip showing the 3 most recent published Journal essays.
 *
 * SEO purpose:
 *   - Routes Home-page traffic into the Journal
 *   - Adds 3 strong internal links from the highest-authority page on the site
 *     to specific essays (high-trust Home → essay-X signal for Google)
 *   - Reinforces editorial freshness on the most-visited page
 *
 * UX purpose:
 *   - Quietly establishes the firm as a research-publishing authority, not just a service provider
 *   - Gives new visitors a sample of the partners' thinking before they commit to a consultation
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ARTICLES, ARTICLE_BODIES } from '@/pages/journal/data';

function pickTopEssays() {
  // Only essays that have real body content (published, not stub).
  const published = (ARTICLES || []).filter((a) => a.bodyKey && ARTICLE_BODIES[a.bodyKey]);
  // Sort by publish date, newest first.
  const sorted = [...published].sort((a, b) => b.date.localeCompare(a.date));
  return sorted.slice(0, 3);
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
}

export const LatestFromJournal = () => {
  const essays = pickTopEssays();

  if (essays.length === 0) return null;

  return (
    <section
      className="bg-[#F6F2EA] py-20 px-4 sm:px-6 lg:px-8"
      data-testid="home-latest-from-journal"
      aria-labelledby="latest-from-journal-heading"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 border-b border-[#0B2240]/15 pb-8">
          <div>
            <p
              className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#A87437] mb-3"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              From The Journal
            </p>
            <h2
              id="latest-from-journal-heading"
              className="text-3xl sm:text-4xl text-[#0B2240] leading-tight tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Research-backed editorial<span className="text-[#A87437]">.</span>
            </h2>
            <p className="text-sm sm:text-base text-[#0B2240]/65 mt-4 max-w-xl font-serif italic leading-relaxed">
              Quarterly essays from our partners on tax, structure, and stewardship.
            </p>
          </div>
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-[#A87437] hover:text-[#0B2240] transition-colors self-start sm:self-auto group"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            data-testid="home-journal-viewall"
          >
            View all essays
            <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
        </div>

        {/* 3 essay cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(essays || []).map((essay) => (
            <Link
              key={essay.id}
              to={`/journal/${essay.id}`}
              className="group flex flex-col bg-white border border-[#0B2240]/10 hover:border-[#A87437]/50 rounded-2xl overflow-hidden transition-all duration-500"
              data-testid={`home-journal-card-${essay.id}`}
            >
              {/* Hero image */}
              <div className="relative aspect-[16/10] bg-[#0B2240] overflow-hidden">
                <img
                  src={essay.image}
                  alt={`${essay.title} illustration`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                {/* Top-left category badge */}
                <div
                  className="absolute top-4 left-4 bg-[#0B2240]/90 backdrop-blur-md px-3 py-1 rounded-full border border-[#D4AF37]/40"
                  style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                >
                  <span className="text-[9px] tracking-[0.16em] uppercase font-bold text-[#D4AF37]">
                    {essay.category}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0B2240]/55 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                  {formatDate(essay.date)} · {essay.readMin} min read
                </div>
                <h3
                  className="text-xl text-[#0B2240] leading-snug font-bold mb-3 group-hover:text-[#A87437] transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {essay.title}
                </h3>
                <p className="text-sm text-[#0B2240]/65 leading-relaxed flex-1 font-serif">
                  {essay.deck}
                </p>
                <div className="mt-5 pt-4 border-t border-[#0B2240]/10 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#0B2240] text-white flex items-center justify-center text-[11px] font-bold" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                    {essay.author.initials}
                  </div>
                  <div className="text-xs">
                    <div className="font-semibold text-[#0B2240]" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                      {essay.author.name}
                    </div>
                    <div className="text-[#0B2240]/55 italic font-serif text-[11px]">
                      {essay.author.role}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
